import useRequest from "api/useRequest";
import {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from "react";
import { Form, Loader, Select, Settings, SettingsItem } from "ui";
import { IPermission, IRoles } from "../interfaces";
import { IFormContext } from "ui/Form/FormContext";
import { accessFetch, accessUpdate, rolesFetch } from "../actions";
import PermissionsTable from "app/Account/Users/tables/PermissionsTable";
import { permissionsFetch } from "app/Account/Users/actions";
import { IFormChangeResponse, IGPFormRef } from "ui/Form/Form";
import { MenuItem } from "@mui/material";
import trans from "helpers/trans";
import { AxiosResponse } from "axios";

type TSimplePermissionsForm = {
    applicationKey: string;
    userId: number;
};

export type TSimplePermissionsFormRef = {
    onSubmit: Function;
};

const SimplePermissionsForm = forwardRef<
    TSimplePermissionsFormRef,
    TSimplePermissionsForm
>(({ applicationKey, userId }, ref) => {
    const formRef = useRef<IGPFormRef | null>();
    const [selected, setSelected] = useState<number[]>([]);
    const [userRole, setUserRole] = useState<number>();
    const [userLoaded, setUserLoaded] = useState<boolean>(false);
    const { request } = useRequest();

    useImperativeHandle(ref, () => ({
        onSubmit: handleSubmitForm,
    }));

    const { currentData: permissions, isPending: isPendingPermissions } =
        useRequest(permissionsFetch(applicationKey));

    const { currentData: rolesData, isPending: isPendingRoles } = useRequest(
        rolesFetch(applicationKey, {
            params: { _sort: { name: "asc" }, _with: "permissions" },
        })
    );

    const { reload: reloadUser } = useRequest(
        accessFetch(
            userId,
            applicationKey,
            {
                params: {
                    _with: ["permissions"],
                },
            },
            (response: AxiosResponse) => {
                if (
                    response.status === 200 &&
                    response.data.data.role !== null
                ) {
                    setUserRole(response.data.data.role_id);
                    setSelected(
                        response.data.data.permissions.map(
                            (item: IPermission) => item.id
                        )
                    );
                }
                setUserLoaded(true);
            }
        )
    );

    useEffect(() => {
        formRef.current?.onChange({ id: "permissions", value: selected });
    }, [selected]);

    const roles = useMemo(() => {
        if (rolesData) {
            return rolesData.data.map((item: IRoles) => ({
                ...item,
                permissions: item.permissions.map(
                    (permission: IPermission) => permission.id
                ),
            }));
        }
        return [];
    }, [rolesData]);

    const handleSubmitForm = (
        event: React.FormEvent<HTMLFormElement | HTMLButtonElement>,
        force: boolean = false
    ) => {
        formRef.current?.onSubmit(event, force);
    };

    const handleOnChangeRole = (
        data: IFormChangeResponse,
        context: IFormContext
    ) => {
        const role = roles.find((item: IRoles) => item.id === data.value);

        if (role === undefined) {
            return;
        }

        context.onChange([
            data,
            { id: "permissions", value: role.permissions },
        ]);
    };

    const handleSubmit = (data: any) => {
        setUserLoaded(false);
        request(
            accessUpdate(userId, applicationKey, data, undefined, (response: AxiosResponse) => {
                if (response.status === 204) {
                    reloadUser();
                }
            })
        );
    };

    if (isPendingPermissions || isPendingRoles || !userLoaded) {
        return <Loader />;
    }

    return (
        <Form
            data={{}}
            fields={{
                _app: { default: applicationKey },
                role_id: { default: userRole },
                permissions: {
                    default: selected,
                },
            }}
            errors={[]}
            onSubmit={handleSubmit}
            ref={formRef}
            unsaved={false}
        >
            {(context: IFormContext) => (
                <>
                    <Settings sx={{ mb: 2 }}>
                        <SettingsItem
                            label={trans(
                                "admin.account.users.userPermissionsForm.role"
                            )}
                        >
                            <Select
                                fullWidth
                                id="role_id"
                                onChange={handleOnChangeRole}
                            >
                                {roles.map((item: IRoles, idx: number) => (
                                    <MenuItem
                                        value={item.id}
                                        key={`role-${idx}`}
                                        id={`role-${idx}`}
                                    >
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </SettingsItem>
                    </Settings>

                    {!!context.data.role_id && (
                        <PermissionsTable
                            permissions={permissions.data}
                            selected={context.data.permissions}
                            onChange={setSelected}
                        />
                    )}
                </>
            )}
        </Form>
    );
});

export default SimplePermissionsForm;
